import { useEffect, useState } from "react";
import { Link, Navigate, useOutletContext } from "react-router-dom";
import { Plus, Trash2, Clock, CalendarClock, CalendarOff, ShieldCheck } from "lucide-react";
import api from "../../lib/api";
import { Card, Badge } from "../../components/ui/Card";
import Button from "../../components/ui/Button";
import Input from "../../components/ui/Input";
import WorkingHours from "../../components/business/WorkingHours";
import SpecialHours from "../../components/business/SpecialHours";

const EMPTY_FORM = { name: "", bio: "" };
const RUNNING_BEHIND_PRESETS = [10, 20, 30];

export default function Staff() {
  const { business } = useOutletContext();
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(EMPTY_FORM);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = () =>
    api
      .get("/owner/staff")
      .then(({ data }) => setStaff(data))
      .catch(() => setStaff([]))
      .finally(() => setLoading(false));

  useEffect(() => {
    load();
  }, []);

  const [addError, setAddError] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setAddError(null);
    try {
      await api.post("/owner/staff", form);
      setForm(EMPTY_FORM);
      setShowForm(false);
      load();
    } catch (err) {
      // 402: the Free plan's staff limit.
      setAddError({ message: err?.response?.data?.message || "Could not add this staff member.", upgrade: err?.response?.status === 402 });
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    await api.delete(`/owner/staff/${id}`);
    load();
  };

  const [updatingId, setUpdatingId] = useState(null);

  const setRunningBehind = async (staffMember, minutes) => {
    setUpdatingId(staffMember.id);
    try {
      await api.post(`/owner/staff/${staffMember.id}/running-behind`, { minutes });
      load();
    } finally {
      setUpdatingId(null);
    }
  };

  const [timeOffStaffId, setTimeOffStaffId] = useState(null);
  const [hoursStaffId, setHoursStaffId] = useState(null);
  const [specialHoursStaffId, setSpecialHoursStaffId] = useState(null);
  const [newTimeOffDate, setNewTimeOffDate] = useState("");

  const addTimeOff = async (staffMember) => {
    if (!newTimeOffDate) return;
    await api.post(`/owner/staff/${staffMember.id}/time-off`, { blocked_date: newTimeOffDate });
    setNewTimeOffDate("");
    load();
  };

  const removeTimeOff = async (staffMember, timeOffId) => {
    await api.delete(`/owner/staff/${staffMember.id}/time-off/${timeOffId}`);
    load();
  };

  const [permissionsStaffId, setPermissionsStaffId] = useState(null);
  const [permissionsForm, setPermissionsForm] = useState({ permission_level: "no_login", commission_type: "none", commission_rate: 0 });

  const openPermissions = (staffMember) => {
    setPermissionsStaffId(permissionsStaffId === staffMember.id ? null : staffMember.id);
    setPermissionsForm({
      permission_level: staffMember.permission_level || "no_login",
      commission_type: staffMember.commission_type || "none",
      commission_rate: staffMember.commission_rate || 0,
    });
  };

  const savePermissions = async (staffMember) => {
    await api.put(`/owner/staff/${staffMember.id}`, permissionsForm);
    setPermissionsStaffId(null);
    load();
  };

  if (business?.business_type === "freelance") return <Navigate to="/business" replace />;

  return (
    <div>
      <div className="mb-4 flex items-center justify-between">
        <h1 className="font-montserrat text-2xl font-bold text-slate-900">Staff</h1>
        <Button size="sm" onClick={() => setShowForm((v) => !v)}>
          <Plus className="h-4 w-4" /> Add staff
        </Button>
      </div>

      {showForm && (
        <Card className="mb-6 p-4">
          <form onSubmit={submit} className="space-y-3">
            <Input placeholder="Full name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            <Input placeholder="Short bio (optional)" value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })} />
            {addError && (
              <p className="text-sm text-destructive">
                {addError.message}{" "}
                {addError.upgrade && (
                  <Link to="/business/subscription" className="font-semibold text-primary underline">
                    See plans
                  </Link>
                )}
              </p>
            )}
            <div className="flex gap-2">
              <Button type="submit" disabled={saving}>{saving ? "Saving…" : "Save staff member"}</Button>
              <Button type="button" variant="outline" onClick={() => setShowForm(false)}>Cancel</Button>
            </div>
          </form>
        </Card>
      )}

      {loading ? (
        <p className="text-muted-foreground">Loading…</p>
      ) : staff.length === 0 ? (
        <p className="text-muted-foreground">No staff yet — add your first team member above.</p>
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {staff.map((s) => (
            <Card key={s.id} className="p-4">
              <div className="mb-2 flex items-center justify-between">
                <p className="font-semibold text-slate-900">{s.name}</p>
                <button onClick={() => remove(s.id)} className="text-muted-foreground hover:text-destructive">
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
              {s.bio && <p className="text-sm text-muted-foreground">{s.bio}</p>}
              <p className="mt-2 text-xs text-muted-foreground">
                ★ {Number(s.rating_avg).toFixed(1)} ({s.rating_count})
              </p>

              <div className="mt-3 border-t border-border pt-3">
                {s.running_behind_minutes > 0 ? (
                  <div className="mb-2 flex items-center justify-between">
                    <Badge variant="warning">
                      <Clock className="mr-1 h-3 w-3" /> Running {s.running_behind_minutes} min behind
                    </Badge>
                    <button
                      className="text-xs text-muted-foreground underline"
                      onClick={() => setRunningBehind(s, 0)}
                      disabled={updatingId === s.id}
                    >
                      Clear
                    </button>
                  </div>
                ) : (
                  <p className="mb-2 text-xs text-muted-foreground">On schedule</p>
                )}
                <div className="flex gap-2">
                  {RUNNING_BEHIND_PRESETS.map((minutes) => (
                    <Button
                      key={minutes}
                      size="sm"
                      variant="outline"
                      disabled={updatingId === s.id}
                      onClick={() => setRunningBehind(s, minutes)}
                    >
                      +{minutes} min
                    </Button>
                  ))}
                </div>
              </div>

              <div className="mt-3 border-t border-border pt-3">
                <button
                  className="flex items-center gap-1 text-xs font-medium text-slate-600 hover:text-slate-900"
                  onClick={() => setHoursStaffId(hoursStaffId === s.id ? null : s.id)}
                >
                  <Clock className="h-3.5 w-3.5" /> Working hours
                </button>
                {hoursStaffId === s.id && (
                  <WorkingHours
                    staffMember={s}
                    onSaved={() => {
                      setHoursStaffId(null);
                      load();
                    }}
                  />
                )}
              </div>

              <div className="mt-3 border-t border-border pt-3">
                <button
                  className="flex items-center gap-1 text-xs font-medium text-slate-600 hover:text-slate-900"
                  onClick={() => setTimeOffStaffId(timeOffStaffId === s.id ? null : s.id)}
                >
                  <CalendarOff className="h-3.5 w-3.5" /> Time off
                </button>
                {timeOffStaffId === s.id && (
                  <div className="mt-2 space-y-2">
                    <div className="flex flex-wrap gap-2">
                      {(s.availability ?? []).filter((a) => a.is_blocked).map((a) => (
                        <Badge key={a.id} variant="default" className="gap-1">
                          {a.blocked_date?.slice(0, 10)}
                          <button onClick={() => removeTimeOff(s, a.id)} className="ml-1 hover:text-destructive">
                            <Trash2 className="h-3 w-3" />
                          </button>
                        </Badge>
                      ))}
                      {(s.availability ?? []).filter((a) => a.is_blocked).length === 0 && (
                        <p className="text-xs text-muted-foreground">No blocked dates.</p>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <Input
                        type="date"
                        value={timeOffStaffId === s.id ? newTimeOffDate : ""}
                        onChange={(e) => setNewTimeOffDate(e.target.value)}
                        className="w-auto"
                      />
                      <Button size="sm" variant="outline" onClick={() => addTimeOff(s)}>Block date</Button>
                    </div>
                  </div>
                )}
              </div>

              <div className="mt-3 border-t border-border pt-3">
                <button
                  className="flex items-center gap-1 text-xs font-medium text-slate-600 hover:text-slate-900"
                  onClick={() => setSpecialHoursStaffId(specialHoursStaffId === s.id ? null : s.id)}
                >
                  <CalendarClock className="h-3.5 w-3.5" /> Special hours
                </button>
                {specialHoursStaffId === s.id && <SpecialHours staffMember={s} />}
              </div>

              <div className="mt-3 border-t border-border pt-3">
                <button
                  className="flex items-center gap-1 text-xs font-medium text-slate-600 hover:text-slate-900"
                  onClick={() => openPermissions(s)}
                >
                  <ShieldCheck className="h-3.5 w-3.5" /> Access & commission
                </button>
                {permissionsStaffId === s.id && (
                  <div className="mt-2 space-y-2">
                    <div>
                      <label className="mb-1 block text-xs text-muted-foreground">Portal access level</label>
                      <select
                        value={permissionsForm.permission_level}
                        onChange={(e) => setPermissionsForm({ ...permissionsForm, permission_level: e.target.value })}
                        className="h-9 w-full rounded-lg border border-input bg-white px-2 text-xs outline-none focus:border-primary"
                      >
                        <option value="no_login">No login</option>
                        <option value="basic">Basic</option>
                        <option value="low">Low</option>
                        <option value="medium">Medium</option>
                        <option value="high">High</option>
                        <option value="owner">Owner-level</option>
                      </select>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <select
                        value={permissionsForm.commission_type}
                        onChange={(e) => setPermissionsForm({ ...permissionsForm, commission_type: e.target.value })}
                        className="h-9 rounded-lg border border-input bg-white px-2 text-xs outline-none focus:border-primary"
                      >
                        <option value="none">No commission</option>
                        <option value="percentage">% of sale</option>
                        <option value="fixed">Fixed per booking</option>
                      </select>
                      <Input
                        type="number"
                        placeholder="Rate"
                        value={permissionsForm.commission_rate}
                        onChange={(e) => setPermissionsForm({ ...permissionsForm, commission_rate: e.target.value })}
                        className="h-9 text-xs"
                      />
                    </div>
                    <Button size="sm" onClick={() => savePermissions(s)}>Save</Button>
                  </div>
                )}
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
